/**
 * Admin Layout with Navigation
 * Wraps admin pages with sidebar and header
 */

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Package,
  ShoppingCart,
  FileText,
  LifeBuoy,
  Globe,
  Bell,
  Download,
  BookOpen,
  Activity,
  DollarSign,
  BarChart3,
  Users2,
  Gift,
  Webhook,
  Coins,
  Languages,
  Calculator,
  Menu,
  X,
  LogOut,
  Settings,
  ChevronDown,
  Flame,
  Server,
} from 'lucide-react';

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: NavItem[];
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['Dashboard']);

  const navigationItems: NavItem[] = [
    {
      label: 'Dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />,
      path: '/admin/dashboard',
    },
    {
      label: 'Clients',
      icon: <Users className="w-5 h-5" />,
      children: [
        { label: 'All Clients', icon: null, path: '/admin/clients' },
        { label: 'Client Groups', icon: null, path: '/admin/client-groups' },
      ],
    },
    {
      label: 'Products',
      icon: <Package className="w-5 h-5" />,
      children: [
        { label: 'All Products', icon: null, path: '/admin/products' },
        { label: 'Product Groups', icon: null, path: '/admin/product-groups' },
        { label: 'Addons', icon: null, path: '/admin/addons' },
      ],
    },
    {
      label: 'Orders',
      icon: <ShoppingCart className="w-5 h-5" />,
      path: '/admin/orders',
    },
    {
      label: 'Invoices',
      icon: <FileText className="w-5 h-5" />,
      path: '/admin/invoices',
    },
    {
      label: 'Services',
      icon: <Settings className="w-5 h-5" />,
      path: '/admin/services',
    },
    {
      label: 'Servers',
      icon: <Server className="w-5 h-5" />,
      path: '/admin/servers',
    },
    {
      label: 'Support',
      icon: <LifeBuoy className="w-5 h-5" />,
      children: [
        { label: 'Tickets', icon: null, path: '/admin/tickets' },
        { label: 'Departments', icon: null, path: '/admin/ticket-departments' },
        { label: 'Canned Responses', icon: null, path: '/admin/canned-responses' },
      ],
    },
    {
      label: 'Domains',
      icon: <Globe className="w-5 h-5" />,
      children: [
        { label: 'All Domains', icon: null, path: '/admin/domains' },
        { label: 'TLD Pricing', icon: null, path: '/admin/tld-pricing' },
        { label: 'Registrars', icon: null, path: '/admin/registrars' },
      ],
    },
    {
      label: 'Marketing',
      icon: <Gift className="w-5 h-5" />,
      children: [
        { label: 'Announcements', icon: null, path: '/admin/announcements' },
        { label: 'Promotions', icon: null, path: '/admin/promotions' },
        { label: 'Affiliates', icon: null, path: '/admin/affiliates' },
        { label: 'Affiliate Reports', icon: null, path: '/admin/affiliate-reports' },
        { label: 'Emails', icon: null, path: '/admin/emails' },
      ],
    },
    {
      label: 'Content',
      icon: <BookOpen className="w-5 h-5" />,
      children: [
        { label: 'Knowledge Base', icon: null, path: '/admin/knowledge-base' },
        { label: 'Downloads', icon: null, path: '/admin/downloads' },
      ],
    },
    {
      label: 'Analytics',
      icon: <BarChart3 className="w-5 h-5" />,
      children: [
        { label: 'Dashboard', icon: null, path: '/admin/analytics' },
        { label: 'Financial Reports', icon: null, path: '/admin/financial-reports' },
        { label: 'Customer Analytics', icon: null, path: '/admin/customer-analytics' },
      ],
    },
    {
      label: 'System',
      icon: <Activity className="w-5 h-5" />,
      children: [
        { label: 'Network Status', icon: null, path: '/admin/network-status' },
        { label: 'Webhooks', icon: null, path: '/admin/webhooks' },
        { label: 'Integrations', icon: null, path: '/admin/integrations' },
        { label: 'Ads Manager', icon: null, path: '/admin/ads' },
        { label: 'ProtectCord', icon: null, path: '/admin/protectcord' },
      ],
    },
    {
      label: 'Settings',
      icon: <Settings className="w-5 h-5" />,
      children: [
        { label: 'General', icon: null, path: '/admin/settings/general' },
        { label: 'Languages', icon: null, path: '/admin/settings/languages' },
        { label: 'Currencies', icon: null, path: '/admin/settings/currencies' },
        { label: 'Tax', icon: null, path: '/admin/settings/tax' },
        { label: 'Payment Gateways', icon: null, path: '/admin/settings/payment-gateways' },
      ],
    },
  ];

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]
    );
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isParentActive = (item: NavItem): boolean => {
    if (item.path) return isActive(item.path);
    if (item.children) return item.children.some((child) => isActive(child.path));
    return false;
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-900/95 backdrop-blur-xl border-b border-slate-700/50 fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all duration-300"
            >
              {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-center gap-2">
              <Flame className="w-6 h-6 text-orange-500" />
              <h1 className="text-xl font-bold bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                PhoenixBill Admin
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button className="relative p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all duration-300">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full"></span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all duration-300"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex pt-14">
        {/* Sidebar */}
        <aside
          className={`fixed left-0 top-14 bottom-0 bg-slate-900/95 backdrop-blur-xl border-r border-slate-700/50 transition-all duration-300 overflow-y-auto ${
            isSidebarOpen ? 'w-64' : 'w-0'
          }`}
        >
          <nav className="p-4 space-y-1">
            {navigationItems.map((item) => (
              <div key={item.label}>
                {item.path ? (
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-300 ${
                      isActive(item.path)
                        ? 'bg-gradient-to-r from-orange-500/20 to-orange-600/10 text-orange-400 border-l-2 border-orange-500 shadow-[0_0_10px_rgba(255,107,53,0.15)]'
                        : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                    }`}
                  >
                    {item.icon}
                    <span className="font-medium">{item.label}</span>
                  </Link>
                ) : (
                  <>
                    <button
                      onClick={() => toggleMenu(item.label)}
                      className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg transition-all duration-300 ${
                        isParentActive(item)
                          ? 'bg-gradient-to-r from-orange-500/20 to-orange-600/10 text-orange-400 border-l-2 border-orange-500 shadow-[0_0_10px_rgba(255,107,53,0.15)]'
                          : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {item.icon}
                        <span className="font-medium">{item.label}</span>
                      </div>
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-300 ${
                          expandedMenus.includes(item.label) ? 'rotate-180' : ''
                        } ${isParentActive(item) ? 'text-orange-400' : ''}`}
                      />
                    </button>
                    {expandedMenus.includes(item.label) && item.children && (
                      <div className="ml-8 mt-1 space-y-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path!}
                            className={`block px-3 py-2 rounded-lg text-sm transition-all duration-300 ${
                              isActive(child.path)
                                ? 'text-orange-400 bg-orange-500/10'
                                : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main
          className={`flex-1 transition-all duration-300 ${
            isSidebarOpen ? 'ml-64' : 'ml-0'
          }`}
        >
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
